<template>
    <div class="min-h-screen flex flex-col items-center justify-center p-4 sm:p-8" :style="rootStyle">

        <!-- ═══════════ LOADING ═══════════ -->
        <div v-if="loadingConfig" class="flex flex-col items-center gap-4">
            <i class="fas fa-circle-notch fa-spin text-3xl" :style="{ color: cfg.colors.primary }"></i>
            <p class="text-sm font-medium" :style="{ color: cfg.colors.textSecondary }">Chargement de votre espace de réservation...</p>
        </div>

        <!-- ═══════════ ERROR ═══════════ -->
        <div v-else-if="loadError" class="text-center p-8 bg-white rounded-3xl shadow-xl max-w-md w-full">
            <i class="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
            <h3 class="text-xl font-bold mb-2">Lien invalide ou expiré</h3>
            <p class="text-gray-500 text-sm">Impossible de charger la configuration. Veuillez contacter votre prestataire.</p>
        </div>

        <!-- ═══════════ FORM ═══════════ -->
        <div v-else class="w-full" :style="{ maxWidth: cfg.layout.maxWidth }">

            <!-- HEADER -->
            <div v-if="cfg.branding.showHeader && step !== 'success'" class="flex flex-col items-center gap-3 mb-8">
                <div v-if="cfg.branding.logo">
                    <img :src="cfg.branding.logo" :alt="cfg.branding.companyName" class="h-14 object-contain" />
                </div>
                <div v-else class="flex items-center gap-3">
                    <div class="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl"
                        :style="{ background: cfg.colors.primary, boxShadow: `0 8px 28px ${cfg.colors.primary}50` }">
                        <i :class="cfg.branding.icon || 'fas fa-fire-flame-curved'"></i>
                    </div>
                    <span class="text-2xl font-black tracking-tight" :style="{ color: cfg.colors.textPrimary, fontFamily: cfg.layout.displayFont || 'inherit' }">
                        {{ cfg.branding.companyName }}
                    </span>
                </div>
                <p v-if="cfg.branding.tagline" class="text-sm text-center m-0 max-w-xs leading-relaxed" :style="{ color: cfg.colors.textSecondary }">
                    {{ cfg.branding.tagline }}
                </p>
            </div>

            <!-- STEPPER -->
            <div v-if="cfg.layout.showSteps && step !== 'success'" class="flex items-center mb-6 px-2">
                <template v-for="(s, i) in STEPS">
                    <button :key="s"
                        class="flex flex-col items-center gap-1.5 bg-transparent border-0 p-0 shrink-0"
                        :class="i < currentIdx ? 'cursor-pointer' : 'cursor-default'"
                        @click="i < currentIdx ? goTo(i) : null">
                        <div class="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center border-2 font-bold" :style="dotStyle(i)">
                            <i v-if="i < currentIdx" class="fas fa-check" style="font-size:9px;"></i>
                            <i v-else :class="stepMeta[s].icon" style="font-size:9px;"></i>
                        </div>
                        <span class="hidden sm:block text-[9px] font-extrabold uppercase tracking-widest whitespace-nowrap"
                            :style="{ color: i <= currentIdx ? cfg.colors.primary : cfg.colors.textSecondary + '55' }">
                            {{ stepMeta[s].label }}
                        </span>
                    </button>
                    <div v-if="i < STEPS.length - 1" :key="'l' + i"
                        class="flex-1 mx-1.5 sm:mx-2 rounded-full overflow-hidden" style="height:2px;"
                        :style="{ background: cfg.colors.border }">
                        <div class="h-full rounded-full" style="transition: width 0.6s cubic-bezier(0.4,0,0.2,1);"
                            :style="{ width: i < currentIdx ? '100%' : '0%', background: cfg.colors.primary }"></div>
                    </div>
                </template>
            </div>

            <!-- MAIN CARD -->
            <div :style="cardStyle">

                <!-- Card Header (hors success) -->
                <div v-if="step !== 'success'" class="px-5 sm:px-6 pt-5 sm:pt-6 pb-4">
                    <div class="flex items-center gap-3">
                        <div class="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" :style="{ background: cfg.colors.primaryLight }">
                            <i :class="stepMeta[step].icon" :style="{ color: cfg.colors.primary }"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="font-black text-base sm:text-lg leading-tight"
                                :style="{ color: cfg.colors.textPrimary, fontFamily: cfg.layout.displayFont || 'inherit' }">
                                {{ stepMeta[step].title }}
                            </div>
                            <div class="text-xs mt-0.5 leading-snug" :style="{ color: cfg.colors.textSecondary }">
                                {{ stepMeta[step].subtitle }}
                            </div>
                        </div>
                        <div class="text-xs font-bold px-2.5 py-1 rounded-full shrink-0"
                            :style="{ background: cfg.colors.primaryLight, color: cfg.colors.primary }">
                            {{ currentIdx + 1 }}/{{ STEPS.length }}
                        </div>
                    </div>
                </div>

                <div v-if="step !== 'success'" class="mx-5 sm:mx-6 h-px" :style="{ background: cfg.colors.border }"></div>

                <!-- Step content with transition -->
                <transition :css="false" mode="out-in" @before-enter="tBeforeEnter" @enter="tEnter" @leave="tLeave">
                    <div :key="step">

                        <!-- ══ STEP: DEVICE ══ -->
                        <div v-if="step === 'device'" class="px-5 sm:px-6 py-5">
                            <div class="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
                                <button v-for="d in cfg.devices" :key="d.id"
                                    class="group flex flex-col items-center gap-2.5 p-4 rounded-2xl border-2 cursor-pointer bg-transparent w-full"
                                    style="transition: all 0.18s ease;"
                                    :style="devBtnStyle(d)"
                                    @click="pickDevice(d)">
                                    <div class="w-14 h-14 rounded-2xl flex items-center justify-center" :style="devIconBgStyle(d)">
                                        <img v-if="d.icon_url" :src="d.icon_url" class="w-9 h-9 object-contain" />
                                        <i v-else :class="d.icon || 'fas fa-fire'" class="text-xl" :style="{ color: form.device_id === d.id ? cfg.colors.primary : cfg.colors.textSecondary }"></i>
                                    </div>
                                    <span class="text-xs font-bold text-center leading-tight" :style="{ color: form.device_id === d.id ? cfg.colors.primary : cfg.colors.textPrimary }">
                                        {{ d.label }}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <!-- ══ STEP: INTERVENTION ══ -->
                        <div v-else-if="step === 'intervention'" class="px-5 sm:px-6 py-5 flex flex-col gap-2.5">
                            <button v-for="t in currentInterventionTypes" :key="t.id"
                                class="flex items-center gap-3.5 px-4 py-3.5 rounded-2xl border-2 cursor-pointer text-left bg-transparent w-full"
                                style="transition: all 0.15s ease;"
                                :style="intBtnStyle(t)"
                                @click="pickIntervention(t)">
                                <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                    :style="{ background: form.intervention_id === t.id ? cfg.colors.primary : cfg.colors.primaryLight, color: form.intervention_id === t.id ? '#fff' : cfg.colors.primary }">
                                    <i :class="typeLogiqueIcon(t.type_logique)"></i>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="text-sm font-bold" :style="{ color: cfg.colors.textPrimary }">{{ t.nom }}</div>
                                    <div v-if="t.description" class="text-xs mt-0.5" :style="{ color: cfg.colors.textSecondary }">{{ t.description }}</div>
                                </div>
                                <i v-if="form.intervention_id === t.id" class="fas fa-check-circle shrink-0" :style="{ color: cfg.colors.primary }"></i>
                            </button>

                            <!-- Notes -->
                            <div class="pt-1">
                                <label class="text-xs font-extrabold uppercase tracking-widest mb-2 block" :style="{ color: cfg.colors.textSecondary }">
                                    {{ cfg.labels.notes || 'Notes complémentaires' }}
                                </label>
                                <textarea v-model="form.notes" rows="2"
                                    class="w-full rounded-2xl p-3 text-sm outline-none border-2 resize-none"
                                    :placeholder="cfg.placeholders.notes || 'Ex : ma chaudière fait du bruit…'"
                                    :style="inputStyle"></textarea>
                            </div>
                        </div>

                        <!-- ══ STEP: CONTACT ══ -->
                        <div v-else-if="step === 'contact'" class="px-5 sm:px-6 py-5 flex flex-col gap-4">
                            <div class="grid grid-cols-2 gap-3">
                                <!-- Prénom -->
                                <div>
                                    <label class="block text-xs font-extrabold uppercase tracking-widest mb-1.5" :style="{ color: cfg.colors.textSecondary }">
                                        {{ cfg.labels.prenom || 'Prénom' }} <span :style="{ color: cfg.colors.primary }">*</span>
                                    </label>
                                    <input v-model="form.client.prenom" type="text"
                                        :placeholder="cfg.placeholders.prenom || 'Jean'"
                                        @blur="touchField('prenom')"
                                        class="w-full rounded-2xl px-4 py-3 text-sm border-2 outline-none transition-all"
                                        :style="fieldStyle('prenom')" />
                                    <p v-if="touched.prenom && errors.prenom" class="text-xs mt-1 ml-1" :style="{ color: cfg.colors.error }">
                                        <i class="fas fa-circle-exclamation mr-1"></i>{{ errors.prenom }}
                                    </p>
                                </div>
                                <!-- Nom -->
                                <div>
                                    <label class="block text-xs font-extrabold uppercase tracking-widest mb-1.5" :style="{ color: cfg.colors.textSecondary }">
                                        {{ cfg.labels.nom || 'Nom' }} <span :style="{ color: cfg.colors.primary }">*</span>
                                    </label>
                                    <input v-model="form.client.nom" type="text"
                                        :placeholder="cfg.placeholders.nom || 'Dupont'"
                                        @blur="touchField('nom')"
                                        class="w-full rounded-2xl px-4 py-3 text-sm border-2 outline-none transition-all"
                                        :style="fieldStyle('nom')" />
                                    <p v-if="touched.nom && errors.nom" class="text-xs mt-1 ml-1" :style="{ color: cfg.colors.error }">
                                        <i class="fas fa-circle-exclamation mr-1"></i>{{ errors.nom }}
                                    </p>
                                </div>
                            </div>

                            <!-- Téléphone -->
                            <div>
                                <label class="block text-xs font-extrabold uppercase tracking-widest mb-1.5" :style="{ color: cfg.colors.textSecondary }">
                                    {{ cfg.labels.telephone || 'Téléphone' }} <span :style="{ color: cfg.colors.primary }">*</span>
                                </label>
                                <input v-model="form.client.telephone" type="tel"
                                    :placeholder="cfg.placeholders.telephone || '06 12 34 56 78'"
                                    @blur="touchField('telephone')"
                                    @input="formatTelephone"
                                    class="w-full rounded-2xl px-4 py-3 text-sm border-2 outline-none transition-all"
                                    :style="fieldStyle('telephone')" />
                                <p v-if="touched.telephone && errors.telephone" class="text-xs mt-1 ml-1" :style="{ color: cfg.colors.error }">
                                    <i class="fas fa-circle-exclamation mr-1"></i>{{ errors.telephone }}
                                </p>
                            </div>

                            <!-- Email -->
                            <div>
                                <label class="block text-xs font-extrabold uppercase tracking-widest mb-1.5" :style="{ color: cfg.colors.textSecondary }">
                                    {{ cfg.labels.email || 'Email' }} <span :style="{ color: cfg.colors.primary }">*</span>
                                </label>
                                <input v-model="form.client.email" type="email"
                                    :placeholder="cfg.placeholders.email || 'jean@mail.com'"
                                    @blur="touchField('email')"
                                    class="w-full rounded-2xl px-4 py-3 text-sm border-2 outline-none transition-all"
                                    :style="fieldStyle('email')" />
                                <p v-if="touched.email && errors.email" class="text-xs mt-1 ml-1" :style="{ color: cfg.colors.error }">
                                    <i class="fas fa-circle-exclamation mr-1"></i>{{ errors.email }}
                                </p>
                            </div>

                            <!-- Réassurance -->
                            <p v-if="cfg.labels.contactReassurance" class="text-xs text-center mt-1 flex items-center justify-center gap-1.5"
                                :style="{ color: cfg.colors.textSecondary }">
                                <i class="fas fa-lock text-xs"></i>
                                {{ cfg.labels.contactReassurance }}
                            </p>
                        </div>

                        <!-- ══ STEP: ADDRESS ══ -->
                        <div v-else-if="step === 'address'" class="px-5 sm:px-6 py-5 flex flex-col gap-3">
                            <div class="relative">
                                <input v-model="form.adresse.adresse1" type="text"
                                    :placeholder="cfg.placeholders.address || 'Numéro et rue'"
                                    @input="onAdresseInput"
                                    class="rounded-2xl px-4 py-3 text-sm border-2 outline-none w-full" :style="inputStyle" />
                                <div v-if="adresseSuggestions.length"
                                    class="absolute z-10 w-full mt-1 rounded-2xl shadow-xl border-2 overflow-hidden"
                                    :style="{ background: cfg.colors.surface, borderColor: cfg.colors.border }">
                                    <div v-for="(item, i) in adresseSuggestions" :key="i"
                                        @click="adresseSelect(item)"
                                        class="px-4 py-3 text-sm cursor-pointer border-b last:border-0 flex items-center gap-2"
                                        :style="{ borderColor: cfg.colors.border }"
                                        @mouseover="$event.currentTarget.style.background = cfg.colors.primaryLight"
                                        @mouseleave="$event.currentTarget.style.background = 'transparent'">
                                        <i class="fas fa-map-marker-alt text-xs" :style="{ color: cfg.colors.primary }"></i>
                                        {{ item._label }}
                                    </div>
                                </div>
                            </div>
                            <!-- <input v-model="form.adresse.adresse1" type="text" placeholder="Numéro et rue" -->
                                <!-- class="rounded-2xl px-4 py-3 text-sm border-2 outline-none" :style="inputStyle" /> -->
                            <div class="grid grid-cols-5 gap-3">
                                <input v-model="form.adresse.code_postal" type="text" placeholder="Code postal"
                                    class="col-span-2 rounded-2xl px-4 py-3 text-sm border-2 outline-none" :style="inputStyle" />
                                <input v-model="form.adresse.ville" type="text" placeholder="Ville"
                                    class="col-span-3 rounded-2xl px-4 py-3 text-sm border-2 outline-none" :style="inputStyle" />
                            </div>
                        </div>

                        <!-- ══ STEP: SUMMARY ══ -->
                        <div v-else-if="step === 'summary'" class="px-5 sm:px-6 py-5 flex flex-col gap-2.5">
                            <div v-for="row in summaryRows" :key="row.key"
                                class="flex items-center gap-3 p-3.5 rounded-2xl border"
                                :style="{ borderColor: cfg.colors.border, background: cfg.colors.background }">
                                <div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" :style="{ background: cfg.colors.primaryLight }">
                                    <i :class="row.icon" :style="{ color: cfg.colors.primary }"></i>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="text-[10px] uppercase font-bold mb-0.5" :style="{ color: cfg.colors.textSecondary }">{{ row.label }}</div>
                                    <div class="text-sm font-semibold truncate" :style="{ color: cfg.colors.textPrimary }">{{ row.value }}</div>
                                </div>
                                <button @click="goTo(row.stepIdx)" class="text-xs underline shrink-0 border-0 bg-transparent cursor-pointer"
                                    :style="{ color: cfg.colors.primary }">
                                    Modifier
                                </button>
                            </div>

                            <!-- Privacy -->
                            <div v-if="cfg.privacy.show && cfg.privacy.text"
                                class="mt-1 p-3 rounded-xl text-xs text-center leading-relaxed"
                                :style="{ background: cfg.colors.primaryLight, color: cfg.colors.textSecondary }">
                                <i class="fas fa-shield-halved mr-1" :style="{ color: cfg.colors.primary }"></i>
                                {{ cfg.privacy.text }}
                            </div>
                        </div>

                        <!-- ══ STEP: SUCCESS ══ -->
                        <div v-else-if="step === 'success'" class="flex flex-col items-center gap-6 px-6 py-12 text-center">
                            <div class="w-20 h-20 rounded-full flex items-center justify-center"
                                :style="{ background: cfg.colors.successLight }">
                                <i class="fas fa-check text-4xl" :style="{ color: cfg.colors.success }"></i>
                            </div>
                            <div>
                                <h3 class="text-2xl font-black" :style="{ color: cfg.colors.textPrimary }">
                                    {{ cfg.labels.successTitle }}
                                </h3>
                                <p class="mt-2 text-sm leading-relaxed" :style="{ color: cfg.colors.textSecondary }">
                                    {{ cfg.labels.successMessage }}
                                </p>
                            </div>
                            <div v-if="cfg.privacy.show && cfg.privacy.text"
                                class="text-xs px-4 py-2 rounded-xl"
                                :style="{ background: cfg.colors.primaryLight, color: cfg.colors.textSecondary }">
                                <i class="fas fa-lock mr-1" :style="{ color: cfg.colors.primary }"></i>
                                {{ cfg.privacy.text }}
                            </div>
                            <button v-if="cfg.layout.showRestartButton" @click="restart"
                                class="text-sm font-bold underline border-0 bg-transparent cursor-pointer"
                                :style="{ color: cfg.colors.primary }">
                                <i class="fas fa-rotate-left mr-1"></i>
                                Faire une nouvelle demande
                            </button>
                        </div>

                    </div>
                </transition>

                <!-- Navigation -->
                <div v-if="step !== 'success'" class="px-5 sm:px-6 pb-5 sm:pb-6 flex gap-3">
                    <button v-if="currentIdx > 0"
                        class="w-12 h-12 rounded-2xl border-2 flex items-center justify-center shrink-0 bg-transparent cursor-pointer"
                        @click="prevStep"
                        :style="{ borderColor: cfg.colors.border, color: cfg.colors.textSecondary }">
                        <i class="fas fa-arrow-left"></i>
                    </button>
                    <button class="flex-1 h-12 rounded-2xl font-bold flex items-center justify-center gap-2 border-0"
                        :disabled="!canGoNext || loadingSubmit"
                        @click="handleNext"
                        :style="nextBtnStyle">
                        <i v-if="loadingSubmit" class="fas fa-spinner fa-spin"></i>
                        <template v-else>
                            <span>{{ nextLabel }}</span>
                            <i v-if="step !== 'summary'" class="fas fa-arrow-right text-xs"></i>
                            <i v-else class="fas fa-paper-plane text-xs"></i>
                        </template>
                    </button>
                </div>
            </div>

            <!-- Powered by -->
            <p v-if="cfg.branding.poweredBy && step !== 'success'"
                class="text-center text-xs mt-4 opacity-40"
                :style="{ color: cfg.colors.textSecondary }">
                Propulsé par <a href="https://termoflow.fr" target="_blank" class="font-bold hover:underline">Termoflow</a>
            </p>
        </div>
    </div>
</template>

<script>
const TIMEOUT_MS = 3500;
const GOUV_URL = 'https://api-adresse.data.gouv.fr/search/';

function fetchWithTimeout(url, ms = TIMEOUT_MS) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// Renomme les labels contenant "PAC" pour le grand public
function humanizeDeviceLabel(label) {
    return label.replace(/\bPAC\b/gi, 'Pompe à chaleur');
}

// Icônes par type_logique
const TYPE_LOGIQUE_ICONS = {
    entretien:       'fas fa-calendar-check',
    depannage:       'fas fa-screwdriver-wrench',
    diagnostic:      'fas fa-magnifying-glass',
    installation:    'fas fa-hammer',
    mise_en_service: 'fas fa-play-circle',
    premiere_visite: 'fas fa-handshake',
};

// Descriptions grand public par type_logique
const TYPE_LOGIQUE_DESCRIPTIONS = {
    entretien:
        'Vérification annuelle de votre appareil : nettoyage, réglages et contrôle de sécurité. Obligatoire pour certains équipements, il prolonge la durée de vie et maintient votre garantie.',
    depannage:
        'Votre appareil est tombé en panne ou fonctionne mal ? Un technicien se déplace rapidement pour identifier et résoudre le problème.',
    diagnostic:
        "Vous remarquez une anomalie mais ne savez pas d'où elle vient ? Le technicien examine votre installation et vous remet un rapport avant toute réparation.",
    installation:
        "Pose complète d'un nouvel équipement par un professionnel certifié : raccordements, mise en place et vérification du bon fonctionnement.",
    mise_en_service:
        "Premier démarrage de votre appareil neuf : le technicien effectue les réglages, vérifie chaque paramètre et vous explique comment l'utiliser au quotidien.",
    premiere_visite:
        "Vous souhaitez un conseil avant de vous engager ? Un technicien visite votre domicile pour évaluer votre installation et vous proposer les solutions adaptées.",
};

const STEPS = ['device', 'intervention', 'contact', 'address', 'summary'];

const STEP_META = {
    device:       { title: 'Votre appareil',      subtitle: 'Quel appareil souhaitez-vous faire entretenir ?', icon: 'fas fa-fire',           label: 'Appareil' },
    intervention: { title: "Type d'intervention",  subtitle: 'De quelle intervention avez-vous besoin ?',       icon: 'fas fa-wrench',         label: 'Besoin'   },
    contact:      { title: 'Vos coordonnées',      subtitle: 'Comment pouvons-nous vous contacter ?',           icon: 'fas fa-user',           label: 'Contact'  },
    address:      { title: "L'adresse",            subtitle: 'Où souhaitez-vous être pris en charge ?',         icon: 'fas fa-map-marker-alt', label: 'Adresse'  },
    summary:      { title: 'Récapitulatif',         subtitle: 'Vérifiez vos informations avant de valider',     icon: 'fas fa-clipboard-list', label: 'Envoi'    },
};

// ── Validateurs ──────────────────────────────────────────────────────────────
const VALIDATORS = {
    nom(v) {
        if (!v || !v.trim()) return 'Le nom est requis.';
        if (v.trim().length < 2) return 'Au moins 2 caractères.';
        return null;
    },
    prenom(v) {
        if (!v || !v.trim()) return 'Le prénom est requis.';
        if (v.trim().length < 2) return 'Au moins 2 caractères.';
        return null;
    },
    telephone(v) {
        if (!v || !v.trim()) return 'Le numéro de téléphone est requis.';
        const digits = v.replace(/[\s.\-]/g, '');
        if (!/^(0[1-9])\d{8}$/.test(digits) && !/^\+33[1-9]\d{8}$/.test(digits))
            return 'Format invalide (ex : 06 12 34 56 78).';
        return null;
    },
    email(v) {
        if (!v || !v.trim()) return 'L\'adresse email est requise.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()))
            return 'Adresse email invalide.';
        return null;
    },
};

export default {
    name: 'ClientReservation',

    data() {
        return {
            STEPS,
            stepMeta: STEP_META,
            step: 'device',
            direction: 1,
            loadingConfig: true,
            loadingSubmit: false,
            loadError: false,

            // Champs "touchés" (l'utilisateur a quitté le champ au moins une fois)
            touched: { nom: false, prenom: false, telephone: false, email: false },

            cfg: {
                branding:     { logo: null, companyName: '', tagline: null, icon: 'fas fa-fire-flame-curved', showHeader: true, poweredBy: true },
                colors:       { primary: '#3B82F6', primaryLight: '#edf0f7', primaryDark: '#c53030', background: '#F9FAFB', surface: '#ffffff', textPrimary: '#111827', textSecondary: '#718096', success: '#38a169', successLight: '#f0fff4', border: '#e8ecf0', error: '#e53e3e' },
                layout:       { maxWidth: '540px', showSteps: true, cardShadow: true, cardRounded: '24px', displayFont: null, showRestartButton: true },
                labels:       { nom: 'Nom', prenom: 'Prénom', telephone: 'Téléphone', email: 'Email', notes: 'Notes', searchAddress: 'Rechercher votre adresse', contactReassurance: '', successTitle: 'Demande envoyée !', successMessage: 'Nous vous contacterons rapidement.', cta: 'Valider', next: 'Continuer' },
                placeholders: { nom: 'Dupont', prenom: 'Jean', telephone: '06 12 34 56 78', email: 'jean@mail.com', notes: '', address: '12 rue des Fleurs…' },
                privacy:      { show: false, text: '' },
                devices:      [],
                interventionsByKey: {}
            },

            adresseQuery:       '',
            adresseSuggestions: [],
            adresseLoading:     false,
            adresseDebounce:    null,

            form: {
                device_id:       null,
                device_key:      null,
                intervention_id: null,
                notes:           '',
                client:  { nom: '', prenom: '', telephone: '', email: '' },
                adresse: { adresse1: '', code_postal: '', ville: '', latitude: null, longitude: null }
            }
        };
    },

    computed: {
        token()      { return this.$route.params.token; },
        currentIdx() { return STEPS.indexOf(this.step); },

        currentInterventionTypes() {
            if (!this.form.device_key) return [];
            return this.cfg.interventionsByKey[this.form.device_key] || [];
        },

        selectedDevice() {
            return this.cfg.devices.find(d => d.id === this.form.device_id) || null;
        },

        selectedIntervention() {
            return this.currentInterventionTypes.find(t => t.id === this.form.intervention_id) || null;
        },

        // Erreurs de validation en temps réel
        errors() {
            return {
                nom:       VALIDATORS.nom(this.form.client.nom),
                prenom:    VALIDATORS.prenom(this.form.client.prenom),
                telephone: VALIDATORS.telephone(this.form.client.telephone),
                email:     VALIDATORS.email(this.form.client.email),
            };
        },

        contactValid() {
            return !this.errors.nom && !this.errors.prenom && !this.errors.telephone && !this.errors.email;
        },

        canGoNext() {
            switch (this.step) {
                case 'device':       return !!this.form.device_id;
                case 'intervention': return !!this.form.intervention_id;
                case 'contact':      return this.contactValid;
                case 'address':      return !!(this.form.adresse.adresse1 && this.form.adresse.ville);
                default:             return true;
            }
        },

        nextLabel() {
            return this.step === 'summary' ? this.cfg.labels.cta : this.cfg.labels.next;
        },

        rootStyle() { return { background: this.cfg.colors.background }; },

        cardStyle() {
            return {
                background:   this.cfg.colors.surface,
                borderRadius: this.cfg.layout.cardRounded,
                border:       `1px solid ${this.cfg.colors.border}`,
                boxShadow:    this.cfg.layout.cardShadow ? '0 24px 64px rgba(0,0,0,0.08)' : 'none',
                overflow:     'hidden'
            };
        },

        inputStyle() {
            return { background: this.cfg.colors.background, borderColor: this.cfg.colors.border, color: this.cfg.colors.textPrimary };
        },

        nextBtnStyle() {
            const active = this.canGoNext && !this.loadingSubmit;
            return {
                background: active ? this.cfg.colors.primary : this.cfg.colors.border,
                color:      active ? '#fff' : this.cfg.colors.textSecondary,
                cursor:     active ? 'pointer' : 'not-allowed',
                opacity:    this.loadingSubmit ? 0.7 : 1,
                transition: 'all 0.2s ease'
            };
        },

        summaryRows() {
            const f = this.form;
            return [
                { key: 'dev', label: 'Appareil',    value: this.selectedDevice?.label,                                              icon: 'fas fa-fire',           stepIdx: 0 },
                { key: 'int', label: 'Intervention', value: this.selectedIntervention?.nom,                                          icon: 'fas fa-wrench',         stepIdx: 1 },
                { key: 'cli', label: 'Contact',      value: `${f.client.prenom} ${f.client.nom} · ${f.client.telephone}`,            icon: 'fas fa-user',           stepIdx: 2 },
                { key: 'adr', label: 'Adresse',      value: `${f.adresse.adresse1}, ${f.adresse.code_postal} ${f.adresse.ville}`,    icon: 'fas fa-map-marker-alt', stepIdx: 3 },
            ].filter(r => r.value && r.value.trim() !== '  ·');
        }
    },

    mounted() {
        this.setFavicon(null);
        document.title = 'Chargement…';
        this.fetchConfig();
    },

    methods: {
        /* ── Config ── */
        fetchConfig() {
            this.loadingConfig = true;
            this.$request.get(`rdv/${this.token}/config`)
                .then(res => {
                    const api = res.api || res;

                    this.cfg.branding = {
                        logo:        api.branding.logo_url    || null,
                        companyName: api.branding.company_name,
                        tagline:     api.branding.tagline     || null,
                        icon:        api.branding.icon        || 'fas fa-fire-flame-curved',
                        showHeader:  !!api.branding.show_header,
                        poweredBy:   !!api.branding.powered_by,
                    };
                    this.cfg.colors       = { ...this.cfg.colors,       ...api.colors      };
                    this.cfg.layout       = { ...this.cfg.layout,       ...api.layout      };
                    this.cfg.labels       = { ...this.cfg.labels,       ...api.labels      };
                    this.cfg.placeholders = { ...this.cfg.placeholders, ...api.placeholders };

                    if (api.privacy) {
                        this.cfg.privacy = { show: !!api.privacy.show, text: api.privacy.text || '' };
                    }

                    // Appareils — renomme PAC → Pompe à chaleur
                    this.cfg.devices = (api.appareils || []).map(a => ({
                        id:       a.id,
                        label:    humanizeDeviceLabel(a.label),
                        key:      a.key,
                        icon_url: a.icon || null,
                        hasFluid: a.has_fluid,
                    }));

                    // Interventions groupées par categorie + description grand public injectée
                    const rawTypes = api.intervention_types || api.interventionTypes || [];
                    const grouped  = {};
                    rawTypes.forEach(t => {
                        if (!grouped[t.categorie]) grouped[t.categorie] = [];
                        grouped[t.categorie].push({
                            ...t,
                            // Description lisible si l'API n'en fournit pas
                            description: t.description || TYPE_LOGIQUE_DESCRIPTIONS[t.type_logique] || null,
                        });
                    });
                    this.cfg.interventionsByKey = grouped;

                    document.title = 'Demande d\'intervention';
                    this.setFavicon(this.cfg.branding.logo);

                    this.loadingConfig = false;
                })
                .catch(() => {
                    this.loadError    = true;
                    this.loadingConfig = false;
                });
        },

        /* ── Navigation ── */
        handleNext() {
            if (this.step === 'contact') {
                // Marquer tous les champs comme touchés pour afficher toutes les erreurs
                this.touched = { nom: true, prenom: true, telephone: true, email: true };
            }
            if (!this.canGoNext) return;
            if (this.step === 'summary') this.submit();
            else this.goTo(this.currentIdx + 1);
        },

        goTo(idx) {
            this.direction = idx < this.currentIdx ? -1 : 1;
            this.step      = STEPS[idx];
        },

        prevStep() {
            if (this.currentIdx > 0) this.goTo(this.currentIdx - 1);
        },

        restart() {
            this.step             = 'device';
            this.touched          = { nom: false, prenom: false, telephone: false, email: false };
            this.form.device_id   = null;
            this.form.device_key  = null;
            this.form.intervention_id = null;
            this.form.notes       = '';
            this.form.client      = { nom: '', prenom: '', telephone: '', email: '' };
            this.form.adresse     = { adresse1: '', code_postal: '', ville: '', latitude: null, longitude: null };
            this.adresseQuery     = '';
        },

        pickDevice(d) {
            this.form.device_id       = d.id;
            this.form.device_key      = d.key;
            this.form.intervention_id = null;
            setTimeout(() => this.goTo(1), 220);
        },

        pickIntervention(t) { this.form.intervention_id = t.id; },

        /* ── Soumission ── */
        submit() {
            this.loadingSubmit = true;
            this.$request.post(`rdv/${this.token}/submit`, { ...this.form, token: this.token })
                .then(() => { this.step = 'success'; })
                .catch(() => { alert("Erreur lors de l'envoi. Veuillez réessayer."); })
                .finally(() => { this.loadingSubmit = false; });
        },

        /* ── Validation contact ── */
        touchField(field) {
            this.$set(this.touched, field, true);
        },

        // Formate automatiquement le téléphone avec des espaces (06 XX XX XX XX)
        formatTelephone() {
            const raw    = this.form.client.telephone.replace(/\D/g, '').slice(0, 10);
            const groups = raw.match(/.{1,2}/g) || [];
            this.form.client.telephone = groups.join(' ');
        },

        // Style d'un champ selon son état de validation
        fieldStyle(field) {
            const hasError = this.touched[field] && this.errors[field];
            return {
                background:  this.cfg.colors.background,
                borderColor: hasError ? this.cfg.colors.error : this.cfg.colors.border,
                color:       this.cfg.colors.textPrimary,
            };
        },

        /* ── Adresse ── */
        onAdresseInput() {
            clearTimeout(this.adresseDebounce);
            if (this.form.adresse.adresse1.length < 3) { this.adresseSuggestions = []; return; }
            this.adresseDebounce = setTimeout(() => this.adresseSearch(), 350);
        },

        async adresseSearch() {
            this.adresseLoading = true;
            try {
                const res  = await fetchWithTimeout(`${GOUV_URL}?q=${encodeURIComponent(this.form.adresse.adresse1)}&limit=5`);
                const data = await res.json();
                this.adresseSuggestions = data.features.map(f => ({
                    _label:   f.properties.label,
                    street:   f.properties.name,
                    postcode: f.properties.postcode,
                    city:     f.properties.city,
                    coords:   f.geometry.coordinates
                }));
            } catch (e) { console.error(e); }
            finally { this.adresseLoading = false; }
        },

        adresseSelect(item) {
            this.form.adresse.adresse1  = item.street;
            this.form.adresse.code_postal = item.postcode;
            this.form.adresse.ville       = item.city;
            this.form.adresse.latitude    = item.coords[1];
            this.form.adresse.longitude   = item.coords[0];
            this.adresseSuggestions = [];
        },

        /* ── Helpers UI ── */
        typeLogiqueIcon(key) { return TYPE_LOGIQUE_ICONS[key] || 'fas fa-tools'; },

        dotStyle(i) {
            const active = i === this.currentIdx;
            const done   = i < this.currentIdx;
            return {
                background:  done ? this.cfg.colors.primary : (active ? this.cfg.colors.primaryLight : 'white'),
                borderColor: (done || active) ? this.cfg.colors.primary : this.cfg.colors.border,
                color:       done ? 'white' : (active ? this.cfg.colors.primary : this.cfg.colors.textSecondary)
            };
        },

        devBtnStyle(d) {
            const sel = this.form.device_id === d.id;
            return { borderColor: sel ? this.cfg.colors.primary : this.cfg.colors.border, background: sel ? this.cfg.colors.primaryLight : 'transparent' };
        },

        devIconBgStyle(d) {
            const sel = this.form.device_id === d.id;
            return { background: sel ? `${this.cfg.colors.primary}18` : this.cfg.colors.background };
        },

        intBtnStyle(t) {
            const sel = this.form.intervention_id === t.id;
            return { borderColor: sel ? this.cfg.colors.primary : this.cfg.colors.border, background: sel ? this.cfg.colors.primaryLight : 'transparent' };
        },

        /* ── Transitions ── */
        tBeforeEnter(el) { el.style.opacity = '0'; el.style.transform = `translateX(${this.direction * 30}px)`; },
        tEnter(el, done) {
            el.offsetHeight;
            el.style.transition = 'all 0.3s ease-out';
            el.style.opacity    = '1';
            el.style.transform  = 'translateX(0)';
            setTimeout(done, 300);
        },
        tLeave(el, done) {
            el.style.transition = 'all 0.2s ease-in';
            el.style.opacity    = '0';
            el.style.transform  = `translateX(${-this.direction * 30}px)`;
            setTimeout(done, 200);
        },

        setFavicon(url) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            if (url) {
                link.href = url;
            } else {
                link.href = 'data:,'; // favicon vide
            }
        },
    }
};
</script>
